import classNames from 'classnames';
import { kebabCase } from 'lodash';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MarketVolatilityNote from 'src/components/Buy/BuyFlow/ExpandedFundList/MarketVolatilityNote';
import CostBasisSnapshot from 'src/components/Sell/CostBasisSnapshot/CostBasisSnapshot';
import EarlyWithdrawalPenalty from 'src/components/Shared/EarlyWithdrawalPenalty/EarlyWithdrawalPenalty';
import NonCoveredShares from 'src/components/Sell/SellFundEditor/NonCoveredShares/NonCoveredShares';
import WhatWillIPayInTaxes from 'src/components/Sell/SellFundEditor/WhatWillIPayInTaxes';
import BankAssociableText from 'src/components/Shared/BankAssociableText/BankAssociableText';
import CostBasisToolTip from 'src/components/Shared/CostBasisToolTip/CostBasisToolTip';
import ExpandCollapse from 'src/components/Shared/ExpandCollapse/ExpandCollapse';
import MultiInput from 'src/components/Shared/MultiInput/MultiInput';
import { SelectableGrid } from 'src/components/Shared/SelectableGrid';
import config from 'src/config/config';
import { showStartDateToolTip } from '../../../utils/automatic/automatic-utils.js';
import {
  START_DATE_TOOL_TIP,
  ANNUAL_PERCENTAGE_DISCLAIMER,
} from 'src/redux/modules/modals/modals.module';
import {
  selectSellDay,
  selectSellMonth,
} from 'src/redux/modules/sellTransaction/actions';
import {
  getSelectedDays,
  getSelectedMonths,
} from 'src/redux/modules/sellTransaction/selectors';
import { ApplicationState } from 'src/redux/modules/types';
import {
  DELIVERY_MAIL_CHECK,
  selectableDays,
  selectableMonths,
  UNIT,
} from 'src/settings/settings';
import { UserFund } from 'src/types';
import { openExternalLinkInPane } from 'src/utils/link-helper';
import styles from './AutomaticSellDistributionAmount.module.scss';

export interface AutomaticSellDistributionAmountProps {
  isSalesforceRep: boolean;
  customerWebViewEnabled: boolean;
  actions;
  deliveryMethod;
  modalsSeen;
  fund: UserFund;
  marketVolatility;
  showHeader?: boolean;
  transactionLimit;
}
export const AutomaticSellDistributionAmount: React.FC<
  AutomaticSellDistributionAmountProps
> = ({
  isSalesforceRep,
  customerWebViewEnabled,
  actions,
  deliveryMethod,
  fund,
  marketVolatility,
  showHeader,
  transactionLimit,
  modalsSeen,
}) => {
  const dispatch = useDispatch();
  const selectedDays = useSelector((state) =>
    getSelectedDays(state as ApplicationState, fund.agreementID)
  );
  const selectedMonths = useSelector((state) =>
    getSelectedMonths(state as ApplicationState, fund.agreementID)
  );
  const {
    openModal,
    updateSellFund,
    updateSellFundAmount,
    updateSellFundUnit,
  } = actions;
  const {
    agreementID,
    closeout,
    fundLimitMessage,
    grossAmount,
    grossAmountError,
    grossUnit,
    isFundAssociable,
    hasEarlyWithdrawalPenalty,
    sweep,
    inFocus,
    nonRetirementLike,
    selected,
    ticker,
  } = fund;

  if (grossUnit === UNIT.PERCENT && !modalsSeen?.ANNUAL_PERCENTAGE_DISCLAIMER) {
    openModal(ANNUAL_PERCENTAGE_DISCLAIMER);
  }
  const taxableDetails = [
    {
      className: 'collapse1',
      heading: 'How are Noncovered Shares redeemed?',
      show: true,
      content: (
        <NonCoveredShares
          coveredShares={fund.coveredShares}
          isAutomatic={true}
          isSweep={sweep}
          uncoveredShares={fund.uncoveredShares}
        />
      ),
    },
    {
      className: 'collapse2',
      heading: 'What will I pay in taxes?',
      show: nonRetirementLike && !(!customerWebViewEnabled && isSalesforceRep),
      content: <WhatWillIPayInTaxes />,
    },
  ];
  const tickerSlug = kebabCase(ticker);
  return (
    <div
      className={classNames(
        styles['container'],
        'distribution-amount-container'
      )}
    >
      {showHeader && (
        <div className={styles['desktop-only']}>
          <h3 className="hidden-xs">Distribution Amount</h3>
        </div>
      )}
      <div
        className={classNames(
          styles['section'],
          `e2e-fund-sell-editor-${tickerSlug}`
        )}
      >
        <div
          className={classNames(
            styles['input-deduction-section'],
            'input-deduction-section'
          )}
        >
          <div className={classNames(styles['input-label'], 'input-label')}>
            Enter an Amount
          </div>
          <div className={styles['flex-container-row']}>
            <MultiInput
              applyBranding={false}
              ariaLabel={'Distribution Amount'}
              autoFocus={selected}
              className={classNames(
                styles['distribution-amount-amount-input'],
                `e2e-input-field-${tickerSlug}`
              )}
              defaultValue={String(grossAmount)}
              disabled={closeout}
              displayErrorMessage={grossAmountError}
              displayValidationMessage={!grossAmountError}
              error={grossAmountError}
              errorClassName={classNames(
                'e2e-input-error',
                `e2e-input-error-${tickerSlug}`
              )}
              id={agreementID}
              inputID={`amt-${agreementID}`}
              selectedInput={grossUnit}
              setFocus={inFocus}
              validationMessage={fundLimitMessage}
              onBlur={() => {
                updateSellFund({
                  agreementID,
                  inFocus: undefined,
                });
              }}
              onInputChange={(amount: number) => {
                updateSellFundAmount({ agreementID, amount });
              }}
              onUnitChange={(unit: UNIT) => {
                updateSellFundUnit({ agreementID, unit });
              }}
            />
          </div>
          <BankAssociableText
            containerClass="retirement"
            showText={
              isFundAssociable && deliveryMethod !== DELIVERY_MAIL_CHECK
            }
          />
        </div>
        <div className={styles['right-panel']}>
          {hasEarlyWithdrawalPenalty && (
            <EarlyWithdrawalPenalty ticker={ticker} />
          )}
          {nonRetirementLike && (
            <React.Fragment>
              <CostBasisSnapshot
                changeAction={() =>
                  openExternalLinkInPane(
                    `${config.url}/usis/my-accounts/profile/cost-basis`
                  )
                }
                customerWebViewEnabled={customerWebViewEnabled}
                fund={fund}
                isSalesforceRep={isSalesforceRep}
                labelComponent={<CostBasisToolTip styles={styles} />}
              />
              <ExpandCollapse details={taxableDetails} />
            </React.Fragment>
          )}
        </div>
      </div>
      <div className={styles['frequency-section-title']}>
        Determine how often you will sell from your investment(s).
      </div>
      <div
        className={classNames(
          styles['frequency-section'],
          `e2e-fund-sell-editor-${tickerSlug}`
        )}
      >
        <div className={styles['frequency-container']}>
          <SelectableGrid
            showSelectAllOption
            allItems={selectableMonths}
            buttonProps={{
              phoneColNum: 3,
              btnSize: 64,
              idPrefix: (id) => `month_button_${id}`,
            }}
            handleSelect={(selected) =>
              dispatch(selectSellMonth(selected, agreementID))
            }
            id={`month-chart-${agreementID}`}
            optionName="Months"
            selectedItems={selectedMonths}
            title="Months"
          />
        </div>
        <div className={styles['date-container']}>
          <SelectableGrid
            allItems={selectableDays}
            buttonProps={{
              phoneColNum: 5,
              btnSize: 40,
              idPrefix: (id) => `day_button_${id}`,
            }}
            handleSelect={(selected) =>
              dispatch(selectSellDay(selected, agreementID))
            }
            id={`day-chart-${agreementID}`}
            limitText="Max: 4 days per month"
            maxSelectCount={4}
            selectedItems={selectedDays}
            showSelectAllOption={false}
            title="Date(s)"
          >
            <div className="rb-fund-editor-disclaimer-txt">
              {!customerWebViewEnabled && isSalesforceRep ? (
                <span>
                  <p>
                    By selecting this service, you acknowledge that automatic
                    transactions will be placed in accordance with the
                    following:
                  </p>
                  <br />
                  <p>
                    Automatic Transactions programs are typically established
                    the following business day, but can take up to three
                    business days to be completed.
                  </p>
                  <br />
                  <p>
                    Except for certain dates as noted below, if one of your
                    chosen dates occurs on a weekend or holiday, the trade will
                    be placed on the next business day at that business day’s
                    Net Asset Value (NAV).
                  </p>
                  <br />
                  <p>
                    <b>Exception:</b> If your chosen date falls either on a day
                    that does not exist on the calendar (ex. February 31st) or
                    on the last weekend of the year and the next business day is
                    the following year, your trade will be placed on the
                    previous business day at that business day’s NAV.
                  </p>
                </span>
              ) : (
                <span>
                  <a
                    className="rb-fund-editor-link college-branded-link"
                    onClick={() =>
                      showStartDateToolTip(openModal, START_DATE_TOOL_TIP)
                    }
                  >
                    View important information
                  </a>{' '}
                  regarding scheduled dates of your automatic transactions.
                </span>
              )}
            </div>
          </SelectableGrid>
        </div>
      </div>
      {sweep && (
        <div className={styles['note-container']}>
          <div className={styles['deduction-list-sweep-note']}>
            <span>Important Note:</span> Before redeeming from a sweep account,
            consider any unsettled orders. Trades settle two business days after
            placement. Unsettled trades are not yet reflected in your current
            available balance.
          </div>
        </div>
      )}
      {marketVolatility?.displayVolatilityMessage &&
        !(!customerWebViewEnabled && isSalesforceRep) && (
          <div className={styles['note-container']}>
            <MarketVolatilityNote />
          </div>
        )}
    </div>
  );
};
